import * as immutable from "immutable";
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Store} from "@ngrx/store";
import {cloneDeep} from 'lodash';
import {map, switchMap, distinctUntilKeyChanged} from 'rxjs/operators';
import {of} from 'rxjs/observable/of';
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {BrowserCacheService} from '@ispm/core/services/browser-cache.service';
import {HttpInterceptorService} from "@ispm/core/services/http-interceptor.service";
import {IPatient} from "@ispm/user/interfaces/patient.interface";
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {IspmHttpClient} from '@ispm/core/services/ispm-http-client';
import {PATIENT_DATA_URL} from '@ispm-client/modules/patient/services/config';
import {PatientData} from '@ispm-shared/modules/patient';
import {USER_ACTIONS} from '@ispm/user/store/user-actions.constant';
import {UserService} from '@ispm/user/services/user.service';
import {ArrayUtils} from "@ispm/shared/utils/Array.utils";

let identifiersToInclude = ["MRN", "EnterpriseID"]

@Injectable()
export class PatientService {
  private _patients = [];

  constructor(
    private http: HttpInterceptorService,
    private browserCacheService: BrowserCacheService,
    private store$: Store<IgenomicsState>,
    private authService: AuthenticationService,
    private _ispmHttpClient : IspmHttpClient,
    private user: UserService
  ) {
  }

  get patients(): any[] {
    return this._patients;
  }

  set patients(value: any[]) {
    this._patients = value;
  }

  getPatientData(term: string) {
    if (!term.trim()) {
      return of();
    }

    return this.http.get<PatientData>(`${PATIENT_DATA_URL}?mrn=${term}`);
  }

  getValidAggIdentifier(aggIdentifier: any){
    let outAggIdentifier = aggIdentifier
    if (aggIdentifier) {
      if (typeof aggIdentifier == 'string') {
        if( aggIdentifier != '') {
          outAggIdentifier = JSON.parse(aggIdentifier);
        }
        else {
          outAggIdentifier = {}
          }
      }
    } else {
      outAggIdentifier = {}
    }

    return outAggIdentifier
  }

  getIdentifierToolTip(aggIdentifier: any){
    let identifierToolTip = {
      showTooltip : false,
      htmlContentIcon : ''
    }
    aggIdentifier = this.getValidAggIdentifier(aggIdentifier)

    if (Object.keys(aggIdentifier).length == 0){
      return identifierToolTip
    }

    let filteredIdentifiers = {}
    let idCount = 0;
    identifiersToInclude.forEach((item)=>{
      if (item in aggIdentifier && !! aggIdentifier[item]){
        idCount += 1
        filteredIdentifiers[item] = aggIdentifier[item]
        if (aggIdentifier[item].includes(",")) {
          filteredIdentifiers[item] = aggIdentifier[item].replace(/,/g, ', ')
          idCount += 1
        }
      }
    })
    if (idCount > 1) {
      identifierToolTip.showTooltip = true;
    }
    if(identifierToolTip.showTooltip){
      let th = "";
      let td = "";
      let index = 0
      const len = Object.keys(filteredIdentifiers).length
      for (const [key, value] of Object.entries(filteredIdentifiers)) {
        if ( index == len -1 ){
            //Table Header
            th += '<th style="text-align: left;padding: 5px 5px 5px 10px;max-width:25rem;">' + key + '</th>';
            // Table row
            td += '<td style="text-align: left;padding: 5px 5px 5px 10px;max-width:25rem;">' + value + '</td>';
        }
        else {
            //Table Header
            th += '<th style="text-align: left;padding: 5px 10px 5px 5px;border-right: 1px solid #335d90;max-width:25rem;">' + key + '</th>';
            // Table row
            td += '<td style="text-align: left;padding: 5px 10px 5px 5px;border-right: 1px solid #335d90;max-width:25rem;">' + value + '</td>';
        }
      }
      identifierToolTip.htmlContentIcon = '<table style="border-collapse: collapse; max-width:25rem;">\n' +
      '  <tr>\n' + th +
      '  </tr>\n' +
      '  <tr>\n' + td +
      '</table>';
    }
    return identifierToolTip;
  }

  storeCurrentPatientAggIdentifiers(patientId) {
    return this.user.getPatient( patientId).
      pipe(ArrayUtils.filterEmpty(),distinctUntilKeyChanged("id"),switchMap((patientObj: any) => {
        let aggIdentifier = patientObj.aggIdentifier
        let patientLinks = this.getPatientLinks(patientObj)
        if(patientLinks.length == 0) {
          return of(aggIdentifier)
        }
        let aggIdentifierList = [patientObj.aggIdentifier]
        let otherIds = patientLinks.join()
        let url = `entity-tree/patient?_id=${otherIds}&_fieldslist=id,aggIdentifier`
        return this._ispmHttpClient.get(url).pipe(
          map(response => {
              for(patientObj of response) {
                aggIdentifierList.push(patientObj.aggIdentifier)
              }
              aggIdentifier = this.mergeAggIdentifiers(aggIdentifierList)
              return aggIdentifier
          })
          )
        }
      ),map((aggIdentifier: any) => {
          this.store$.dispatch({
            type: USER_ACTIONS.SET_CURRENT_PATIENT_IDENTIFIERS,
            payload: {
              data : aggIdentifier
            }
          });
      })
      )
  }

  mergeLungRADSScore(lungRADSScoreList: any){
    return lungRADSScoreList.sort((item1, item2)=> item1.reportDate > item2.reportDate ? 1 : -1)[0];
  }

  mergeOpenReportAndScore(openReportList: any){
    let mergedOpenReportAndScore = {}

    for(let openReport of openReportList) {
      for ( let openReportKey of Object.keys(openReport)){
        if(mergedOpenReportAndScore[openReportKey]==null){
          mergedOpenReportAndScore[openReportKey] = [];
        }
          if ( Array.isArray(openReport[openReportKey])){
            mergedOpenReportAndScore[openReportKey].push(...openReport[openReportKey])
          } else if ( typeof(openReport[openReportKey]=='number')){
            if(parseInt(mergedOpenReportAndScore[openReportKey])){
              mergedOpenReportAndScore[openReportKey] = parseInt(mergedOpenReportAndScore[openReportKey]) + parseInt(openReport[openReportKey]);
            }else{
              mergedOpenReportAndScore[openReportKey] =  parseInt(openReport[openReportKey]);
            }
          }
      }
    }
    return mergedOpenReportAndScore
  }

  mergePatientData(itemList: any, aggregationType: string, key: string){
    let mergeditem = {}

    switch(aggregationType){
      case "latest":
        mergeditem = itemList.sort((item1, item2)=> item1[key] > item2[key] ? 1 : -1)[0];
        break;
      case "array-append":
        for(let item of itemList) {
          for ( let itemKey of Object.keys(item)){
            if(mergeditem[itemKey]==null){
              mergeditem[itemKey] = [];
            }

            if ( Array.isArray(item[itemKey])){
              mergeditem[itemKey].push(...item[itemKey])
            } else if ( typeof(item[itemKey]=='number')){
              if(parseInt(mergeditem[itemKey])){
                mergeditem[itemKey] = parseInt(mergeditem[itemKey]) + parseInt(item[itemKey]);
              } else {
                mergeditem[itemKey] =  parseInt(item[itemKey]);
              }
            }
          }
        }
        break;
      // case "text-append":
      //   mergeditem = this.mergeAggIdentifiers(itemList)
      //   break;
      default:
        mergeditem = itemList;
        break;
    }
    return mergeditem
  }

  mergeAggIdentifiers(aggIdentifiersList: any){
    let mergedAggIdentifier = {}
    let outputAggIdentifier = {}

    for(let identifier of identifiersToInclude){
        mergedAggIdentifier[identifier] = []
        outputAggIdentifier[identifier] = {}
    }

    for(let aggIdentifier of aggIdentifiersList) {
        let validatedAggIdentifier = this.getValidAggIdentifier(aggIdentifier)
        for ( let identifier of Object.keys(validatedAggIdentifier)){
            if ( identifiersToInclude.includes(identifier)){
                mergedAggIdentifier[identifier].push(aggIdentifier[identifier])
            }
        }
    }

    for(let identifier of identifiersToInclude){
        outputAggIdentifier[identifier] = mergedAggIdentifier[identifier].join()
    }
    return outputAggIdentifier
  }

  getPatientLinks(patientObj: any) {
    return this.getPatientLinksFromResource(patientObj, "otherKnownIds")
  }

  getPatientLinksStr(patientObj: any){
    return this.getPatientLinks(patientObj).join()
  }

  getPatientLinksFromResource(resource: any, otherKnownIdsKey: string) {
    let patientLinks = []
    let otherKnownIds = resource[otherKnownIdsKey]
    if( otherKnownIdsKey in resource && !!otherKnownIds) {
        if (typeof otherKnownIds == 'string' && otherKnownIds != '' ) {
            otherKnownIds = JSON.parse(otherKnownIds);
          }
        if(Array.isArray(otherKnownIds) && otherKnownIds.length > 0) {
          patientLinks = otherKnownIds
        }
      }
    return patientLinks
  }

  getPatientLinksWithPrimaryPatient(patientObj: any) {
    let patientLinksWithPrimay = [patientObj.id]
    let patientLinks = this.getPatientLinks(patientObj)
    patientLinksWithPrimay.push(...patientLinks)

    return patientLinksWithPrimay
  }

  getPatientIDStrWithPrimary(patientObj: any) {
    let patientLinks = this.getPatientLinksWithPrimaryPatient(patientObj)

    return patientLinks.join()
  }

  getUniqueLinkPatientIdsStr(resourceList: any, patientIdKey: string, otherKnownIdsKey: string) {
    let patientLinkSet = new Set();
    let patientIdSet = new Set();
    let patientLinkStr = "";

    if(resourceList == null || resourceList.length <= 0){
      return patientLinkStr;
    }

    for(let resource of resourceList){
      patientIdSet.add(resource[patientIdKey])
      let patientLinks = this.getPatientLinksFromResource(resource, otherKnownIdsKey)
      patientLinks.forEach((item) => patientLinkSet.add(item));
    }
    //remove the primary patientIds from the patientLinkSet
    for (const elem of patientIdSet) {
      patientLinkSet.delete(elem);
    }
    if(patientLinkSet.size > 0){
      let otherPatientsIds = Array.from(patientLinkSet)
      patientLinkStr = otherPatientsIds.join()
    }
    return patientLinkStr
  }

  groupPatientsByLink(patientList: any) {
    let filteredPatientList = []  // result patient list with the latest patient per group
    let patientSet = new Set()
    for(let patient of patientList) {
      if (!patientSet.has(patient.id)) {
        let patientIdGroup = this.getPatientLinksWithPrimaryPatient(patient)
        let latestPatient = this.getLatestPatientFromGroup(patientIdGroup, patientList)
        filteredPatientList.push(latestPatient)
        patientIdGroup.forEach(patientId=>patientSet.add(patientId))
      }
    }
    return filteredPatientList
  }

  private getLatestPatientFromGroup(patientIdGroup: any, patientList: any) {
    let firstPatient = patientList.find(patient => patient.id == patientIdGroup[0])
    let maxLastUpdated = new Date(firstPatient.meta.lastUpdated)
    let latestPatient = firstPatient
    let identifierList = [latestPatient.aggIdentifier]
    for (let index = 1; index < patientIdGroup.length; index++) {
        let patientId = patientIdGroup[index]
        let patient = patientList.find(patient => patient.id == patientId)
        if(patient) {
          identifierList.push(patient.aggIdentifier)
          let lastUpdated = new Date(patient.meta.lastUpdated)
          if ( lastUpdated.getTime() > maxLastUpdated.getTime() ){
              latestPatient = patient
          }
        }
    }
    latestPatient.mergedIdentifiers = this.mergeAggIdentifiers(identifierList)
    return latestPatient
  }

  getPatientKeysFromResource(data: any){
    let resourceType = data.resourceType;
    const keyValuesObject: {[key: string]: string } = {};
    keyValuesObject.patientIdKey = "id";
    keyValuesObject.otherKnownIdsKey = "otherKnownIds";
    keyValuesObject.aggIdentifierKey = "aggIdentifier";

    if( resourceType === "WorkListRow" ) {
      keyValuesObject.patientIdKey = 'DiagnosticOrder#Patient';
      keyValuesObject.otherKnownIdsKey = 'DiagnosticOrder#Patient#otherKnownIds';
      keyValuesObject.aggIdentifierKey = 'DiagnosticOrder#Patient#aggIdentifier';
      keyValuesObject.openReport = 'DiagnosticOrder#Patient#Events#Open';
      keyValuesObject.lungRADSScore = 'DiagnosticOrder#Observations#RADIOLOGY.LDCT.LUNG#last';
    } else if( resourceType === "UIPractWorkListRow" ) {
      keyValuesObject.patientIdKey = 'patient_id';
      keyValuesObject.otherKnownIdsKey = 'patient_otherKnownIds';
      keyValuesObject.aggIdentifierKey = 'aggIdentifier';
    }

    return keyValuesObject
  }

  get(id: string) {
    let patientDetails = null;
    const patientStoreSubs = this.store$.select('users', 'patients')
      .subscribe((patientData: immutable.Map<string, IPatient>) => {
        let patients = patientData;

        if (patients.has(id)) {
          patientDetails = patients.get(id);
        }
      });

    if (patientStoreSubs) {
      patientStoreSubs.unsubscribe();
    }

    if (patientDetails && Object.keys(patientDetails).length > 0) { //caches {} object
      return Observable.of(patientDetails);
    }

    return this.http.get('/patient/' + id + '?cacheId=' + this.browserCacheService.getCacheId(), {}, true)
      .pipe(
        map(res => res.json())
      );
  }

  getAll(searchVal?:any, resultsAcceptor?:any) {
    if (!searchVal) {
      searchVal = '';
    }

    let params = {name: searchVal, _fieldslist: "id,name,birthDate,identifier", _sort: "family", _count: 1000};

    if (this.authService.user.role != "GenomicsAdminGroup") {
      //commented because of the need to search patient by authorization
      //params.organization = authService.user.info.practitionerRole[0].managingOrganization;
    }

    return this.http.get('/patient', params).pipe(map((res) => {

      // if resultsAcceptor is provided, activate it to indicate if to accept the returned result
      let acceptResults = !resultsAcceptor ||
        resultsAcceptor(res.body) === true;

      if (acceptResults) {
        this._patients = cloneDeep(res.body);
      }
      return this._patients;
    }));
  }

  getAllCustom(searchVal: string, fields: string, organization, criteria: any) {
    if (!searchVal) {
      searchVal = '';
    }

    let params = {name: searchVal, _fieldslist: fields, _fsort: "family", _count: 1000};

    // if criteria parameter is given, append it to the <params> object
    if (criteria && criteria.length > 0) {
      let crList = criteria.split(',');
      for (let i = 0; i < crList.length; i++) {
        let indexOf = crList[i].indexOf('=');
        if (indexOf > 0) {
          let paramName = crList[i].substring(0, indexOf).trim();
          params[paramName] = crList[i].substring(indexOf + 1); // add parameter's value
        }
      }
    }
    return this.http.get('/patient', params).pipe(map((res) => this._patients = cloneDeep(res.body)));
  };

  create(patient: any) {
    // DEPRECATED --> now all patient create requests are routed to the generic API controller
    // return $http.post('/patient', patient)
    patient.resourceType = 'Patient';
    let mrn = patient.identifier[0].value;
    return this.http.post('/api/v2/patient', {
      'resource': patient,
      'clientSpecificMap': [{'identifier': {'unique': 'true'}, 'key': 'MRN', 'value': mrn}]
    })
      .pipe(map((res) => {

        const response = {
          message: null,
          status: res.status,
          data: res.body,
          created: false,
          duplicatedResource: false
        };

        if (response.status === 201) {
          response.created = true;
          response.message = 'Patient Created Successfully!';

          // add new created patient to the list of patients
          this.patients.push(response.data);

        } else if (response.status === 202) {
          response.duplicatedResource = true;
          response.message = 'Cannot create patient since patient with the same MRN is already defined!';

        } else { // ???
          response.message = 'Patient was not created';
        }

        return response;
      }));
  }

  search(searchCriteria: any) {
    searchCriteria._count= 1000;
    return this.http.get('/patient', {params: searchCriteria}, true);
  }

  newPatient() {
    return {
      identifier: [
        {
          use: "usual",
          type: {
            coding: [
              {
                system: "http://hl7.org/fhir/v2/0203",
                code: "MRN",
                display: "MRN"
              }
            ]
          },
          system: "urn:oid:1.2.36.146.595.217.0.1",
          value: "",
          period: {
            start: "",
            end: ""
          },
          assigner: {
            display: "",
            reference: ""
          }
        },
        {
          use: "official",
          type: {
            coding: [
              {
                system: "http://hl7.org/fhir/v2/0203",
                code: "LR",
                display: "Local Registry ID"
              }
            ]
          },
          system: "urn:oid:1.2.36.146.595.217.0.1",
          value: "",
          period: {
            start: "",
            end: ""
          },
          assigner: {
            display: "Ministry Of Interior",
            reference: ""
          }
        },
        {
          use: "official",
          type: {
            coding: [
              {
                system: "http://hl7.org/fhir/v2/0203",
                code: "ACSN",
                display: "Accession Identifier"
              }
            ]
          },
          system: "urn:oid:1.2.36.146.595.217.0.1",
          value: "",
          period: {
            start: "",
            end: ""
          },
          assigner: {
            display: "Hospital",
            reference: ""
          }
        },
        {
          use: "official",
          type: {
            coding: [
              {
                system: "http://hl7.org/fhir/v2/0203",
                code: "DL",
                display: "Driver license number"
              }
            ]
          },
          system: "urn:oid:1.2.36.146.595.217.0.1",
          value: "",
          period: {
            start: "",
            end: ""
          },
          assigner: {
            display: "Ministry Of Transportation",
            reference: ""
          }
        },
        {
          use: "official",
          type: {
            coding: [
              {
                system: "http://hl7.org/fhir/v2/0203",
                code: "PPN",
                display: "Passport number"
              }
            ]
          },
          system: "urn:oid:1.2.36.146.595.217.0.1",
          value: "",
          period: {
            start: "",
            end: ""
          },
          assigner: {
            display: "Ministry Of Interior",
            reference: ""
          }
        }
      ],
      active: true,
      name: [
        {
          use: "official",
          text: "",
          family: [],
          given: [],
          prefix: [],
          suffix: [],
          period: {
            start: "",
            end: ""
          }
        }
      ],
      telecom: [
        {
          system: "phone",
          use: "mobile",
          value: "",
          rank: 1,
          period: {
            start: "",
            end: ""
          }
        },
        {
          system: "phone",
          use: "home",
          value: "",
          rank: 1,
          period: {
            start: "",
            end: ""
          }
        },
        {
          system: "email",
          use: "",
          value: "",
          rank: 1,
          period: {
            start: "",
            end: ""
          }
        }
      ],
      gender: "male",
      birthDate: "",
      deceasedBoolean: false,
      deceasedDateTime: "",
      address: [
        {
          use: "home",
          type: "physical",
          text: "",
          line: [],
          city: "",
          district: "",
          state: "",
          postalCode: "",
          country: "",
          period: {
            start: "",
            end: ""
          }
        }
      ],
      martialStatus: {
        coding: [
          {
            system: "http://hl7.org/fhir/v3/MaritalStatus",
            code: "UNK",
            display: "Unknown"
          }
        ]
      },
      multipleBirthBoolean: false,
      multipleBirthInteger: "",
      photo: [
        {
          contentType: "",
          language: "",
          data: "",
          url: "/assets/img/wallpaper-for-facebook-profile-photo.jpg",
          size: "",
          hash: "",
          title: "",
          creation: ""
        }
      ],
      contact: [],
      communication: [
        {
          language: {
            coding: [
              {
                system: "urn:ietf:bcp:47",
                code: "EN",
                display: "English"
              }
            ]
          },
          preferred: true
        }
      ],
      careProvider: [
        {
          reference: "",
          display: ""
        }
      ],
      managingOrganization: {
        reference: "",
        display: ""
      },
      link: [],
      record: [],
      associated_file: [],
      order: [],
      demographics: {
        race: {
          code: "",
          display: ""
        },
        ethnicity: {
          code: "",
          display: ""
        }
      }
    }
  }
}
